import { useState, useEffect } from "react";
import { Award } from "lucide-react";
import { SelectField } from "../components/ui/select-field.js";
import { api, getAccessToken, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useLookup } from "../context/LookupContext.js";
import { AsyncButton } from "../components/ui/async-button.js";

import { HR_ROLES } from "@hrms/types";
import { usePageBack } from "../context/PageBackContext.js";

function decodeToken(token: string): { role: string; sub: string } {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return { role: String(p["role"] ?? ""), sub: String(p["sub"] ?? "") };
  } catch { return { role: "", sub: "" }; }
}

interface Course {
  id: string; title: string; description: string | null;
  category: string; isMandatory: boolean; isActive: boolean;
}
interface Enrollment {
  id: string; employeeId: string; courseId: string; status: string;
  enrolledAt: string; dueDate: string | null;
  course: Course;
  completion: { id: string; passed: boolean; score: string | null; certificate?: { id: string; certNumber: string } } | null;
}

const inputStyle: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "14px", background: "var(--color-surface-0)", width: "100%",
};
const btnStyle: React.CSSProperties = {
  padding: "8px 16px", borderRadius: "var(--radius-md)", fontSize: "13px",
  fontWeight: 600, cursor: "pointer", border: "none",
  background: "var(--color-brand-600)", color: "#fff",
};

const statusColor = (s: string): React.CSSProperties => {
  if (s === "COMPLETED") return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
  if (s === "FAILED")    return { background: "var(--color-error-bg)", color: "var(--color-error-fg)" };
  if (s === "IN_PROGRESS") return { background: "var(--color-info-bg)", color: "var(--color-info-fg)" };
  return { background: "var(--color-surface-100)", color: "var(--color-text-700)" };
};

const TABS = ["Courses", "My Enrollments", "All Enrollments"] as const;
type Tab = typeof TABS[number];

export default function LearningPage() {
  usePageBack("/dashboard");
  const courseCategories = useLookup("COURSE_CATEGORY");
  const accessToken = getAccessToken() ?? "";
  const { role, sub } = decodeToken(accessToken);
  const isHR = HR_ROLES.has(role);

  const [tab, setTab] = useState<Tab>("Courses");
  const [courses, setCourses] = useState<Course[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "GENERAL", isMandatory: false });
  const [saving, setSaving] = useState(false);

  async function loadCourses() {
    setLoading(true);
    try {
      const r = await api.get<Course[]>("/courses");
      setCourses(r.data);
    } catch (err) { logError("LearningPage.loadCourses", err); setError("Failed to load courses"); }
    finally { setLoading(false); }
  }

  async function loadEnrollments(all = false) {
    setLoading(true);
    try {
      const params = all ? "" : `?employeeId=${sub}`;
      const r = await api.get<{ data: Enrollment[] }>(`/enrollments${params}`);
      setEnrollments(r.data.data);
    } catch (err) { logError("LearningPage.loadEnrollments", err); setError("Failed to load enrollments"); }
    finally { setLoading(false); }
  }

  useEffect(() => {
    if (tab === "Courses") loadCourses();
    else if (tab === "My Enrollments") loadEnrollments(false);
    else if (tab === "All Enrollments" && isHR) loadEnrollments(true);
  }, [tab]);

  async function createCourse() {
    setSaving(true); setError("");
    try {
      await api.post("/courses", { ...form });
      setShowForm(false);
      setForm({ title: "", description: "", category: "GENERAL", isMandatory: false });
      loadCourses();
    } catch (err) { logError("LearningPage.createCourse", err); setError("Failed to create course"); throw err; }
    finally { setSaving(false); }
  }

  async function enroll(courseId: string) {
    try {
      await api.post("/enrollments", { courseId });
      loadEnrollments(false);
      setTab("My Enrollments");
    } catch (e: unknown) {
      const msg = errMsg(e);
      setError(msg ?? "Failed to enroll");
    }
  }

  async function markInProgress(enrollmentId: string) {
    try {
      await api.patch(`/enrollments/${enrollmentId}/status`, { status: "IN_PROGRESS" });
      loadEnrollments(tab === "All Enrollments" && isHR);
    } catch (err) { logError("LearningPage.markInProgress", err); setError("Failed to update status"); }
  }

  async function complete(enrollmentId: string) {
    try {
      await api.post(`/enrollments/${enrollmentId}/complete`, { passed: true });
      loadEnrollments(tab === "All Enrollments" && isHR);
    } catch (e: unknown) {
      const msg = errMsg(e);
      setError(msg ?? "Failed to complete");
    }
  }

  async function downloadCert(certId: string) {
    try {
      const r = await api.get<{ url: string }>(`/certificates/${certId}/url`);
      window.open(r.data.url, "_blank");
    } catch (err) { logError("LearningPage.downloadCert", err); setError("Failed to get certificate URL"); }
  }

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: "8px 16px", borderRadius: "var(--radius-md)", fontSize: "13px",
    fontWeight: active ? 700 : 500, cursor: "pointer",
    border: "1px solid " + (active ? "var(--color-brand-600)" : "var(--color-surface-200)"),
    background: active ? "var(--color-brand-600)" : "var(--color-surface-0)",
    color: active ? "#fff" : "var(--color-surface-700)",
  });

  return (
    <div style={{ padding: "32px", maxWidth: "1100px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h1 style={{ fontSize: "22px", fontWeight: 700 }}>Learning & Development</h1>
        {isHR && tab === "Courses" && (
          <button style={btnStyle} onClick={() => setShowForm(true)}>+ New Course</button>
        )}
      </div>

      {error && (
        <div style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "10px 14px", borderRadius: "var(--radius-md)", marginBottom: "16px", fontSize: "13px" }}>
          {error} <button style={{ float: "right", background: "none", border: "none", cursor: "pointer", color: "var(--color-error-fg)" }} onClick={() => setError("")}>×</button>
        </div>
      )}

      {showForm && isHR && (
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "20px", marginBottom: "20px" }}>
          <h2 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px" }}>New Course</h2>
          <div style={{ display: "grid", gap: "12px" }}>
            <input style={inputStyle} placeholder="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            <textarea style={{ ...inputStyle, resize: "vertical", height: "80px" }} placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            <SelectField value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {courseCategories.map(o => <option key={o.value} value={o.value}>{o.displayName}</option>)}
            </SelectField>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
              <input type="checkbox" checked={form.isMandatory} onChange={e => setForm(f => ({ ...f, isMandatory: e.target.checked }))} />
              Mandatory for all employees
            </label>
          </div>
          <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
            <AsyncButton onClick={() => createCourse()} disabled={saving}>Create</AsyncButton>
            <button style={{ ...btnStyle, background: "var(--color-surface-200)", color: "var(--color-surface-700)" }} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: "8px", marginBottom: "24px" }}>
        {TABS.filter(t => t !== "All Enrollments" || isHR).map(t => (
          <button key={t} style={tabBtnStyle(tab === t)} onClick={() => setTab(t as Tab)}>{t}</button>
        ))}
      </div>

      {loading && <p>Loading…</p>}

      {!loading && tab === "Courses" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "16px" }}>
          {courses.map(c => (
            <div key={c.id} style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                <span style={{ fontSize: "12px", background: "var(--color-info-bg)", color: "var(--color-info-fg-strong)", padding: "2px 8px", borderRadius: "999px" }}>{c.category}</span>
                {c.isMandatory && <span style={{ fontSize: "12px", background: "var(--color-warning-bg)", color: "var(--color-warning-fg)", padding: "2px 8px", borderRadius: "999px" }}>MANDATORY</span>}
              </div>
              <h3 style={{ fontSize: "15px", fontWeight: 600, marginBottom: "6px" }}>{c.title}</h3>
              {c.description && <p style={{ fontSize: "13px", color: "var(--color-surface-500)", marginBottom: "12px" }}>{c.description}</p>}
              <button style={{ ...btnStyle, fontSize: "12px", padding: "6px 12px" }} onClick={() => enroll(c.id)}>
                Enroll
              </button>
            </div>
          ))}
          {courses.length === 0 && <p style={{ fontSize: "13px", color: "var(--color-surface-500)" }}>No courses yet.</p>}
        </div>
      )}

      {!loading && (tab === "My Enrollments" || tab === "All Enrollments") && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <thead>
            <tr style={{ background: "var(--color-surface-50)" }}>
              {["Course", "Category", "Status", "Due Date", "Actions"].map(h => (
                <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600, borderBottom: "1px solid var(--color-surface-200)" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {enrollments.map(e => (
              <tr key={e.id} style={{ borderBottom: "1px solid var(--color-surface-100)" }}>
                <td style={{ padding: "10px 12px" }}>{e.course.title}</td>
                <td style={{ padding: "10px 12px" }}>{e.course.category}</td>
                <td style={{ padding: "10px 12px" }}>
                  <span style={{ ...statusColor(e.status), padding: "2px 8px", borderRadius: "999px", fontSize: "12px" }}>{e.status}</span>
                </td>
                <td style={{ padding: "10px 12px" }}>{e.dueDate ? new Date(e.dueDate).toLocaleDateString() : "—"}</td>
                <td style={{ padding: "10px 12px" }}>
                  <div style={{ display: "flex", gap: "6px" }}>
                    {e.status === "ENROLLED" && (
                      <button style={{ ...btnStyle, fontSize: "12px", padding: "4px 10px", background: "var(--color-info-accent)" }} onClick={() => markInProgress(e.id)}>
                        Start
                      </button>
                    )}
                    {e.status === "IN_PROGRESS" && (
                      <button style={{ ...btnStyle, fontSize: "12px", padding: "4px 10px", background: "var(--color-success)" }} onClick={() => complete(e.id)}>
                        Complete
                      </button>
                    )}
                    {e.completion?.certificate && (
                      <button style={{ ...btnStyle, fontSize: "12px", padding: "4px 10px", background: "var(--color-warning)", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => downloadCert(e.completion!.certificate!.id)}>
                        <Award size={12} /> Certificate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {enrollments.length === 0 && (
              <tr><td colSpan={5} style={{ padding: "24px", textAlign: "center", color: "var(--color-surface-500)" }}>No enrollments.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
